import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';
import walletService from '../services/walletService';
import { buildApiUrl, toUploadUrl } from '../config/api';
import { couponAPI, reviewAPI } from '../services/api';
import '../styles/camping-detail.css';

const CampingDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [site, setSite] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeImage, setActiveImage] = useState(0);
    const [checkIn, setCheckIn] = useState('');
    const [checkOut, setCheckOut] = useState('');
    const [guests, setGuests] = useState(2);
    const [availability, setAvailability] = useState(null);
    const [showBooking, setShowBooking] = useState(false);
    const [processingPayment, setProcessingPayment] = useState(false);
    
    // ✅ Wallet states
    const [useWallet, setUseWallet] = useState(false);
    const [walletBalance, setWalletBalance] = useState(0);
    
    const [bookingData, setBookingData] = useState({
        name: '',
        email: '',
        phone: '',
        special_requests: ''
    });
    const [couponCode, setCouponCode] = useState('');
    const [couponLoading, setCouponLoading] = useState(false);
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [couponMessage, setCouponMessage] = useState('');
    const [reviews, setReviews] = useState([]);
    const [reviewMeta, setReviewMeta] = useState({ count: 0, average_rating: 0 });
    const [reviewForm, setReviewForm] = useState({ rating: 5, review: '', customer_name: '', customer_email: '' });
    const [reviewSubmitting, setReviewSubmitting] = useState(false);
    const [reviewMessage, setReviewMessage] = useState('');

    const fetchWalletBalance = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            if (token) {
                const result = await walletService.getBalance();
                if (result.success) {
                    setWalletBalance(result.balance);
                }
            }
        } catch (error) {
            console.error('Error fetching wallet balance:', error);
        }
    }, []);

    const fetchSite = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(buildApiUrl(`/camping/site/${slug}`));
            
            if (response.data.success && response.data.data) {
                setSite(response.data.data);
            } else {
                setSite(null);
            }
        } catch (error) {
            console.error('Error fetching camping site:', error);
            setSite(null);
        } finally {
            setLoading(false);
        }
    }, [slug]);

    useEffect(() => {
        fetchSite();
        fetchWalletBalance();
        window.scrollTo(0, 0);
    }, [fetchSite, fetchWalletBalance]);

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            try {
                const user = JSON.parse(userData);
                setReviewForm((prev) => ({
                    ...prev,
                    customer_name: user.name || user.username || '',
                    customer_email: user.email || ''
                }));
            } catch {
                // ignore
            }
        }
    }, []);

    useEffect(() => {
        if (!site?.id) return;

        const loadReviews = async () => {
            try {
                const response = await reviewAPI.getReviews('camping', site.id);
                setReviews(response.data?.data || []);
                setReviewMeta(response.data?.meta || { count: 0, average_rating: 0 });
            } catch (error) {
                console.error('Error fetching camping reviews:', error);
            }
        };

        loadReviews();
    }, [site?.id]);

    const checkAvailability = async () => {
        if (!checkIn || !checkOut || !site) return;
        
        try {
            const response = await axios.get(
                `${buildApiUrl(`/camping/check-availability/${site.id}`)}?check_in=${checkIn}&check_out=${checkOut}`
            );
            setAvailability(response.data);
            return response.data;
        } catch (error) {
            console.error('Error checking availability:', error);
            return null;
        }
    };

    const calculateNights = () => {
        if (!checkIn || !checkOut) return 0;
        const diffTime = Math.abs(new Date(checkOut) - new Date(checkIn));
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const calculateBaseTotal = () => {
        if (!site) return 0;
        const nights = calculateNights();
        const price = site.discount_price || parseFloat(site.price_per_night);
        return nights * price;
    };

    const calculateTotal = () => {
        const baseTotal = calculateBaseTotal();
        const discount = Math.min(Number(appliedCoupon?.amount || 0), baseTotal);
        return Math.max(baseTotal - discount, 0);
    };

    // ✅ Calculate payable amount after wallet deduction
    const calculatePayableAmount = () => {
        const total = calculateTotal();
        if (useWallet) {
            const walletDeduction = Math.min(walletBalance, total);
            return total - walletDeduction;
        }
        return total;
    };

    const handleApplyCoupon = async () => {
        const normalizedCode = couponCode.trim().toUpperCase();
        if (!normalizedCode) {
            setCouponMessage('Please enter a coupon code.');
            return;
        }

        try {
            setCouponLoading(true);
            setCouponMessage('');
            const response = await couponAPI.validateCoupon(normalizedCode, calculateBaseTotal());
            const coupon = response.data?.data || null;
            setAppliedCoupon(coupon);
            setCouponCode(coupon?.code || normalizedCode);
            setCouponMessage(`Coupon ${coupon?.code} applied. You saved ₹${Number(coupon?.amount || 0).toLocaleString('en-IN')}.`);
        } catch (error) {
            setAppliedCoupon(null);
            setCouponMessage(error.response?.data?.message || 'Unable to apply coupon.');
        } finally {
            setCouponLoading(false);
        }
    };

    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
        setCouponCode('');
        setCouponMessage('Coupon removed.');
    };

    const loadRazorpayScript = () => {
        return new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const createBooking = async (paymentDetails = null) => {
        try {
            const bookingPayload = {
                site_id: site.id,
                customer_name: bookingData.name,
                customer_email: bookingData.email,
                customer_phone: bookingData.phone,
                check_in: checkIn,
                check_out: checkOut,
                guests,
                total_nights: calculateNights(),
                total_amount: calculateTotal(),
                original_amount: calculateBaseTotal(),
                advance_amount: calculateTotal(),
                special_requests: bookingData.special_requests,
                payment_id: paymentDetails?.razorpay_payment_id,
                payment_status: paymentDetails ? 'completed' : 'pending',
                wallet_amount: useWallet ? walletAmount : 0,
                coupon_id: appliedCoupon?.id || null,
                coupon_code: appliedCoupon?.code || null,
                coupon_discount: Number(appliedCoupon?.amount || 0)
            };
           
            const response = await axios.post(buildApiUrl('/camping/book'), bookingPayload);
            return response.data;
        } catch (error) {
            console.error('Error creating booking:', error);
            throw error;
        }
    };

    // ✅ Apply wallet payment
    const applyWalletPayment = async (bookingId) => {
        if (!useWallet || walletAmount === 0) return true;
        
        try {
            const token = localStorage.getItem('token');
            const result = await axios.post(
                buildApiUrl('/wallet/apply-payment'),
                {
                    amount: walletAmount,
                    bookingId: bookingId,
                    bookingType: 'camping'
                },
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            return result.data.success;
        } catch (error) {
            console.error('Wallet payment error:', error);
            return false;
        }
    };

    const sendBookingEmail = async (bookingDetails) => {
        try {
            await axios.post(buildApiUrl('/send-booking-email'), {
                bookingId: bookingDetails.bookingId,
                customerEmail: bookingData.email,
                customerName: bookingData.name,
                siteName: site.name,
                checkIn,
                checkOut,
                guests: guests,
                nights: calculateNights(),
                totalAmount: calculateTotal(),
                walletUsed: useWallet ? walletAmount : 0,
                payableAmount: calculatePayableAmount(),
                specialRequests: bookingData.special_requests
            });
            console.log('✅ Booking email sent successfully');
        } catch (error) {
            console.error('Error sending email:', error);
        }
    };

    const initiatePayment = async () => {
        if (!bookingData.name || !bookingData.email || !bookingData.phone) {
            alert('Please fill in all required fields');
            return false;
        }

        setProcessingPayment(true);

        try {
            const payableAmount = calculatePayableAmount();
            
            // If payable amount is 0 (fully paid by wallet)
            if (payableAmount === 0) {
                // Create booking without Razorpay
                const bookingResult = await createBooking();
                
                // Apply wallet payment
                await applyWalletPayment(bookingResult.id);
                
                // Send email
                await sendBookingEmail(bookingResult);
                
                alert(`✅ Booking successful! Your booking ID: ${bookingResult.bookingId}\n\nA confirmation email has been sent to ${bookingData.email}`);
                
                // Reset form
                setShowBooking(false);
                setCheckIn('');
                setCheckOut('');
                setAvailability(null);
                setUseWallet(false);
                setBookingData({
                    name: '',
                    email: '',
                    phone: '',
                    special_requests: ''
                });
                
                // Redirect to success page
                navigate(`/camping-booking-success/${bookingResult.bookingId}`, {
                    state: {
                        bookingId: bookingResult.bookingId,
                        type: 'camping',
                        siteName: site.name,
                        siteImage: site.image,
                        location: `${site.location_name}, ${site.location_address}`,
                        checkIn,
                        checkOut,
                        guests: guests,
                        nights: calculateNights(),
                        totalAmount: calculateTotal(),
                        walletUsed: walletAmount,
                        payableAmount: 0,
                        customerName: bookingData.name,
                        customerEmail: bookingData.email,
                        customerPhone: bookingData.phone
                    }
                });
                
                setProcessingPayment(false);
                return true;
            }

            // Load Razorpay script
            const isScriptLoaded = await loadRazorpayScript();
            if (!isScriptLoaded) {
                alert('Failed to load payment gateway. Please try again.');
                setProcessingPayment(false);
                return false;
            }

            const nights = calculateNights();

            const orderResponse = await axios.post(buildApiUrl('/payments/camping/create-order'), {
                amount: payableAmount,
                currency: 'INR',
                receipt: `camping_${site.id}_${Date.now()}`,
                site_id: site.id,
                site_name: site.name,
                check_in: checkIn,
                check_out: checkOut,
                guests: guests,
                nights: nights,
                customer_name: bookingData.name,
                customer_email: bookingData.email,
                customer_phone: bookingData.phone,
                wallet_used: useWallet,
                wallet_amount: walletAmount
            });

            console.log('Order Response:', orderResponse.data);

            if (!orderResponse.data.success) {
                alert('Failed to create payment order');
                setProcessingPayment(false);
                return false;
            }

            const order = orderResponse.data.order;
            const keyId = orderResponse.data.key_id;

            const options = {
                key: keyId || process.env.REACT_APP_RAZORPAY_KEY_ID,
                amount: order.amount,
                currency: order.currency,
                name: 'RedGoTrip',
                description: `Camping Booking: ${site.name} - ${nights} nights${useWallet ? ` (Wallet: ₹${walletAmount})` : ''}`,
                order_id: order.id,
                handler: async (response) => {
                    console.log('Payment success handler called:', response);
                    
                    try {
                        // Step 1: Create booking
                        const bookingResult = await createBooking({
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_order_id: response.razorpay_order_id
                        });
                        
                        console.log('Booking created:', bookingResult);

                        // Step 2: Apply wallet payment if used
                        if (useWallet && walletAmount > 0) {
                            await applyWalletPayment(bookingResult.id);
                        }

                        // Step 3: Verify payment
                        const verifyResponse = await axios.post(buildApiUrl('/payments/camping/verify'), {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                            booking_id: bookingResult.id
                        });

                        console.log('Verify response:', verifyResponse.data);

                        if (verifyResponse.data.success) {
                            // Step 4: Send email notification
                            await sendBookingEmail(bookingResult);
                            
                            alert(`✅ Booking successful! Your booking ID: ${bookingResult.bookingId}\n\nA confirmation email has been sent to ${bookingData.email}`);
                            
                            // Reset form
                            setShowBooking(false);
                            setCheckIn('');
                            setCheckOut('');
                            setAvailability(null);
                            setUseWallet(false);
                            setBookingData({
                                name: '',
                                email: '',
                                phone: '',
                                special_requests: ''
                            });

                            // Redirect to success page
                            navigate(`/camping-booking-success/${bookingResult.bookingId}`, {
                                state: {
                                    bookingId: bookingResult.bookingId,
                                    type: 'camping',
                                    siteName: site.name,
                                    siteImage: site.image,
                                    location: `${site.location_name}, ${site.location_address}`,
                                    checkIn,
                                    checkOut,
                                    guests: guests,
                                    nights: nights,
                                    totalAmount: calculateTotal(),
                                    walletUsed: walletAmount,
                                    payableAmount: payableAmount,
                                    customerName: bookingData.name,
                                    customerEmail: bookingData.email,
                                    customerPhone: bookingData.phone
                                }
                            });
                        } else {
                            alert('Payment verification failed: ' + (verifyResponse.data.message || 'Unknown error'));
                        }
                    } catch (error) {
                        console.error('Payment handler error:', error);
                        console.error('Error details:', error.response?.data);
                        alert('Payment processing failed: ' + (error.response?.data?.message || error.message));
                    }
                    setProcessingPayment(false);
                },
                prefill: {
                    name: bookingData.name,
                    email: bookingData.email,
                    contact: bookingData.phone
                },
                notes: {
                    booking_type: 'camping',
                    site_name: site.name,
                    site_id: site.id,
                    check_in: checkIn,
                    check_out: checkOut,
                    guests: guests,
                    nights: nights,
                    wallet_used: useWallet,
                    wallet_amount: walletAmount
                },
                theme: {
                    color: '#2e7d32'
                },
                modal: {
                    ondismiss: () => {
                        console.log('Payment modal dismissed');
                        setProcessingPayment(false);
                    }
                }
            };

            const razorpay = new window.Razorpay(options);
            
            razorpay.on('payment.failed', (response) => {
                console.error('Payment failed:', response.error);
                alert('Payment failed: ' + response.error.description);
                setProcessingPayment(false);
            });
            
            razorpay.open();
            return true;
        } catch (error) {
            console.error('Payment initiation error:', error);
            alert(error.response?.data?.message || 'Failed to initiate payment. Please try again.');
            setProcessingPayment(false);
            return false;
        }
    };

    const handleBooking = async (e) => {
        e.preventDefault();
        
        if (!bookingData.name || !bookingData.email || !bookingData.phone) {
            alert('Please fill in all required fields');
            return;
        }

        const availabilityCheck = await checkAvailability();
        if (availabilityCheck && !availabilityCheck.data?.available) {
            alert('Site is no longer available for selected dates. Please choose different dates.');
            return;
        }

        await initiatePayment();
    };

    const handleReviewSubmit = async (e) => {
        e.preventDefault();
        if (!reviewForm.customer_name || !reviewForm.review) {
            setReviewMessage('Please add your name and review.');
            return;
        }

        try {
            setReviewSubmitting(true);
            await reviewAPI.createReview({
                entity_type: 'camping',
                entity_id: site.id,
                customer_name: reviewForm.customer_name,
                customer_email: reviewForm.customer_email,
                rating: reviewForm.rating,
                review: reviewForm.review
            });
            setReviewMessage('Review submitted successfully. It will appear after admin approval.');
            setReviewForm((prev) => ({ ...prev, review: '', rating: 5 }));
        } catch (error) {
            setReviewMessage(error.response?.data?.message || 'Failed to submit review.');
        } finally {
            setReviewSubmitting(false);
        }
    };

    const getImageUrl = (imagePath) => {
        return toUploadUrl(imagePath, {
            camping: true,
            fallback: 'https://redgotrips.com/images/default-camping.jpg'
        });
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(price);
    };

    if (loading) {
        return (
            <div className="loading-screen">
                <div className="loading-spinner"></div>
                <p>Discovering your perfect camping spot...</p>
            </div>
        );
    }

    if (!site) {
        return (
            <div className="error-screen">
                <div className="error-icon">🏕️</div>
                <h2>Campsite Not Found</h2>
                <p>The campsite you're looking for doesn't exist or has been removed.</p>
                <Link to="/camping" className="back-home-btn">Browse All Campsites</Link>
            </div>
        );
    }

    const images = site.gallery ? site.gallery.split(',').filter(img => img.trim()) : [];
    const currentPrice = site.discount_price || parseFloat(site.price_per_night);
    const totalAmount = calculateTotal();
    const baseTotalAmount = calculateBaseTotal();
    const couponDiscount = Math.min(Number(appliedCoupon?.amount || 0), baseTotalAmount);
    const walletAmount = useWallet ? Math.min(walletBalance, totalAmount) : 0;
    const payableAmount = calculatePayableAmount();
    const nights = calculateNights();
    const locationText = `${site.location_name}, ${site.location_address}`;
    const reviewsCount = reviewMeta.count || reviews.length || 0;
    const heroAverageRating = Number(reviewMeta.average_rating || 0) > 0
        ? Number(reviewMeta.average_rating).toFixed(1)
        : Number(site.rating || 0) > 0
            ? Number(site.rating).toFixed(1)
            : '';
    const amenitiesCount = [
        site.has_electricity,
        site.has_water,
        site.has_wifi,
        site.has_parking,
        site.has_fire_pit,
        site.pet_friendly
    ].filter(Boolean).length + (site.bed_type ? 1 : 0);

    return (
        <div className="camping-detail">
            <SEO
                title={site ? `${site.name} | Camping Stay | RedGoTrip` : 'Camping Details | RedGoTrip'}
                description={site ? `${site.name} in ${site.location_name || site.region || 'India'}. Check campsite photos, pricing, reviews, availability and book online with RedGoTrip.` : 'Explore camping stay details, pricing and availability on RedGoTrip.'}
                canonicalPath={site ? `/camping/${site.slug || slug}` : `/camping/${slug}`}
                keywords={site ? `${site.name}, camping stay, ${site.location_name || ''}, RedGoTrip camping` : 'camping details, RedGoTrip'}
                image={site?.main_image ? toUploadUrl(site.main_image, { camping: true }) : undefined}
                jsonLd={site ? {
                    '@context': 'https://schema.org',
                    '@type': 'Campground',
                    name: site.name,
                    description: site.description || `${site.name} camping stay`,
                    image: site.main_image ? toUploadUrl(site.main_image, { camping: true }) : undefined,
                    address: {
                        '@type': 'PostalAddress',
                        addressLocality: site.location_name || '',
                        addressRegion: site.region || ''
                    }
                } : null}
            />
            <div className="camping-page-container">
                <div className="hero-breadcrumb">
                        <Link to="/">Home</Link>
                        <span>/</span>
                        <Link to="/camping">Camping</Link>
                        <span>/</span>
                        <span className="current">{site.name}</span>
                </div>

                <div className="camp-hero-grid">
                    <div className="camp-hero-copy card-surface">
                        <div className="camp-badge-row">
                            <span className="camp-badge">
                                <i className="fas fa-campground"></i>
                                Camping Stay
                            </span>
                            {site.pet_friendly === 1 && (
                                <span className="camp-offer-badge">Pet Friendly</span>
                            )}
                        </div>
                        <h1 className="hero-title">{site.name}</h1>
                        <div className="hero-location">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                <circle cx="12" cy="10" r="3"></circle>
                            </svg>
                            <span>{locationText}</span>
                        </div>
                        <div className="hero-stats">
                            <div className="stat">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="12" cy="7" r="4"></circle>
                                </svg>
                                <span>Up to {site.capacity} Guests</span>
                            </div>
                            {site.altitude && (
                                <div className="stat">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M12 3v18M8 7l4-4 4 4M8 17l4 4 4-4"></path>
                                    </svg>
                                    <span>{site.altitude}</span>
                                </div>
                            )}
                            {(heroAverageRating || reviewsCount > 0) && (
                                <div className="stat rating">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                                    </svg>
                                    <span>{heroAverageRating || '4.5'} rating{reviewsCount > 0 ? ` • ${reviewsCount} review${reviewsCount === 1 ? '' : 's'}` : ''}</span>
                                </div>
                            )}
                        </div>
<p className="camp-summary-text">
  {site.description?.split(" ").slice(0, 100).join(" ")}
  {site.description?.split(" ").length > 100 ? "..." : ""}
</p>                        <div className="camp-hero-actions">
                            <button className="check-availability-btn hero-check-btn" onClick={() => setShowBooking(true)}>
                                Check Availability
                            </button>
                            <a href="tel:+919335444047" className="camp-secondary-btn">
                                <i className="fas fa-phone-alt"></i>
                                Call Expert
                            </a>
                        </div>
                    </div>

                    <div className="camp-hero-media card-surface">
                        <div className="gallery-main hero-gallery-main">
                            <img src={getImageUrl(images[activeImage] || site.image)} alt={site.name} />
                            {images.length > 1 && (
                                <>
                                    <button
                                        type="button"
                                        className="camp-gallery-nav prev-nav"
                                        onClick={() => setActiveImage((prev) => (prev === 0 ? images.length - 1 : prev - 1))}
                                        aria-label="Previous image"
                                    >
                                        <i className="fas fa-chevron-left"></i>
                                    </button>
                                    <button
                                        type="button"
                                        className="camp-gallery-nav next-nav"
                                        onClick={() => setActiveImage((prev) => (prev === images.length - 1 ? 0 : prev + 1))}
                                        aria-label="Next image"
                                    >
                                        <i className="fas fa-chevron-right"></i>
                                    </button>
                                </>
                            )}
                            <div className="camp-image-caption">
                                <strong>{site.location_name}</strong>
                                <span>{Math.max(images.length, 1)} campsite views</span>
                            </div>
                        </div>
                        {images.length > 1 && (
                            <div className="gallery-thumbs hero-gallery-thumbs">
                                {images.map((img, index) => (
                                    <button
                                        key={index}
                                        className={`thumb ${activeImage === index ? 'active' : ''}`}
                                        onClick={() => setActiveImage(index)}
                                    >
                                        <img src={getImageUrl(img)} alt={`View ${index + 1}`} />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="camp-facts-bar card-surface">
                    <div className="camp-fact-item">
                        <i className="fas fa-bed"></i>
                        <div>
                            <span className="camp-fact-label">Stay Style</span>
                            <strong>{site.bed_type || 'Camping setup'}</strong>
                        </div>
                    </div>
                    <div className="camp-fact-item">
                        <i className="fas fa-list-check"></i>
                        <div>
                            <span className="camp-fact-label">Amenities</span>
                            <strong>{amenitiesCount} highlights</strong>
                        </div>
                    </div>
                    <div className="camp-fact-item">
                        <i className="fas fa-users"></i>
                        <div>
                            <span className="camp-fact-label">Capacity</span>
                            <strong>Up to {site.capacity} guests</strong>
                        </div>
                    </div>
                    <div className="camp-fact-item">
                        <i className="fas fa-star"></i>
                        <div>
                            <span className="camp-fact-label">Reviews</span>
                            <strong>{reviewsCount} guest reviews</strong>
                        </div>
                    </div>
                </div>

                <div className="content-container">
                <div className="content-grid">
                    <div className="main-content">
                        <div className="card">
                            <h2 className="section-title">About This Campsite</h2>
                            <p className="description-text">{site.description}</p>
                            {site.location_description && (
                                <>
                                    <h3 className="subsection-title">The Location</h3>
                                    <p className="description-text">{site.location_description}</p>
                                </>
                            )}
                        </div>

                        <div className="card">
                            <h2 className="section-title">Amenities & Facilities</h2>
                            <div className="amenities-grid">
                                {site.has_electricity === 1 && (
                                    <div className="amenity">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M13 2L3 14h8l-2 8 10-12h-8l2-8z"></path>
                                        </svg>
                                        <span>Electricity</span>
                                    </div>
                                )}
                                {site.has_water === 1 && (
                                    <div className="amenity">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"></path>
                                        </svg>
                                        <span>Water Supply</span>
                                    </div>
                                )}
                                {site.has_wifi === 1 && (
                                    <div className="amenity">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M5 12.55a11 11 0 0 1 14.08 0"></path>
                                            <path d="M1.42 9a16 16 0 0 1 21.16 0"></path>
                                            <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
                                            <line x1="12" y1="20" x2="12.01" y2="20"></line>
                                        </svg>
                                        <span>WiFi</span>
                                    </div>
                                )}
                                {site.has_parking === 1 && (
                                    <div className="amenity">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M7 17h10M12 7v10M7 7h10"></path>
                                            <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                                        </svg>
                                        <span>Parking</span>
                                    </div>
                                )}
                                {site.has_fire_pit === 1 && (
                                    <div className="amenity">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"></path>
                                        </svg>
                                        <span>Fire Pit</span>
                                    </div>
                                )}
                                {site.pet_friendly === 1 && (
                                    <div className="amenity">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path>
                                        </svg>
                                        <span>Pet Friendly</span>
                                    </div>
                                )}
                                {site.bed_type && (
                                    <div className="amenity">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M3 9v6M21 9v6M7 9h10M3 15h18"></path>
                                            <rect x="3" y="5" width="18" height="4" rx="1"></rect>
                                        </svg>
                                        <span>{site.bed_type}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {(reviews.length > 0 || true) && (
                            <div className="card">
                                <h2 className="section-title">Guest Reviews</h2>
                                {reviews.length > 0 ? (
                                <div className="reviews-list">
                                    {reviews.map((review, index) => (
                                        <div key={review.id || index} className="review">
                                            <div className="review-header">
                                                <div className="reviewer-info">
                                                    <div className="reviewer-avatar">
                                                        {review.customer_name?.charAt(0) || 'G'}
                                                    </div>
                                                    <div>
                                                        <div className="reviewer-name">{review.customer_name || 'Anonymous'}</div>
                                                        <div className="review-stars">
                                                            {[...Array(5)].map((_, i) => (
                                                                <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill={i < review.rating ? "#fbbf24" : "#e2e8f0"}>
                                                                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                                                                </svg>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="review-date">
                                                    {new Date(review.created_at).toLocaleDateString('en-IN', {
                                                        day: 'numeric',
                                                        month: 'short',
                                                        year: 'numeric'
                                                    })}
                                                </div>
                                            </div>
                                            <p className="review-text">{review.review}</p>
                                        </div>
                                    ))}
                                </div>
                                ) : (
                                    <p className="description-text">No approved reviews yet. Share your experience after your stay.</p>
                                )}

                                <form className="review-form" onSubmit={handleReviewSubmit}>
                                    <h3 className="subsection-title">Write a review</h3>
                                    <div className="review-form-grid">
                                        <input
                                            type="text"
                                            className="booking-input"
                                            placeholder="Your name"
                                            value={reviewForm.customer_name}
                                            onChange={(e) => setReviewForm((prev) => ({ ...prev, customer_name: e.target.value }))}
                                        />
                                        <input
                                            type="email"
                                            className="booking-input"
                                            placeholder="Your email"
                                            value={reviewForm.customer_email}
                                            onChange={(e) => setReviewForm((prev) => ({ ...prev, customer_email: e.target.value }))}
                                        />
                                        <select
                                            className="guest-select"
                                            value={reviewForm.rating}
                                            onChange={(e) => setReviewForm((prev) => ({ ...prev, rating: Number(e.target.value) }))}
                                        >
                                            {[5, 4, 3, 2, 1].map((rating) => <option key={rating} value={rating}>{rating} Star</option>)}
                                        </select>
                                    </div>
                                    <textarea
                                        rows="4"
                                        className="booking-textarea"
                                        placeholder="Share your camping experience"
                                        value={reviewForm.review}
                                        onChange={(e) => setReviewForm((prev) => ({ ...prev, review: e.target.value }))}
                                    ></textarea>
                                    {reviewMessage && <div className={`coupon-message ${reviewSubmitting ? '' : 'success'}`}>{reviewMessage}</div>}
                                    <button type="submit" className="pay-btn" disabled={reviewSubmitting}>
                                        {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
                                    </button>
                                </form>
                            </div>
                        )}
                    </div>

                    <div className="booking-sidebar">
                        <div className="booking-card">
                            <div className="booking-topline">
                                <span className="camp-sidebar-badge">
                                    <i className="fas fa-bolt"></i>
                                    Instant booking support
                                </span>
                            </div>
                            <div className="price-section">
                                {site.discount_price ? (
                                    <>
                                        <span className="original-price">{formatPrice(site.price_per_night)}</span>
                                        <span className="current-price">{formatPrice(site.discount_price)}</span>
                                    </>
                                ) : (
                                    <span className="current-price">{formatPrice(site.price_per_night)}</span>
                                )}
                                <span className="per-night">/ night</span>
                            </div>

                            <div className="camp-booking-facts">
                                <div className="camp-booking-fact">
                                    <span>Location</span>
                                    <strong>{site.location_name}</strong>
                                </div>
                                <div className="camp-booking-fact">
                                    <span>Guests</span>
                                    <strong>1 to {site.capacity}</strong>
                                </div>
                            </div>

                            {!showBooking ? (
                                <button className="check-availability-btn" onClick={() => setShowBooking(true)}>
                                    Check Availability
                                </button>
                            ) : (
                                <div className="booking-form">
                                    <div className="form-group">
                                        <label>Check-in Date</label>
                                        <input
                                            type="date"
                                            value={checkIn}
                                            onChange={(e) => {
                                                const nextCheckIn = e.target.value;
                                                setCheckIn(nextCheckIn);
                                                if (checkOut && nextCheckIn && checkOut < nextCheckIn) {
                                                    setCheckOut('');
                                                }
                                            }}
                                            min={new Date().toISOString().split('T')[0]}
                                            className="date-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Check-out Date</label>
                                        <input
                                            type="date"
                                            value={checkOut}
                                            onChange={(e) => setCheckOut(e.target.value)}
                                            min={checkIn || new Date().toISOString().split('T')[0]}
                                            className="date-input"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Number of Guests</label>
                                        <select value={guests} onChange={(e) => setGuests(parseInt(e.target.value))} className="guest-select">
                                            {[...Array(site.capacity || 6)].map((_, i) => (
                                                <option key={i + 1} value={i + 1}>
                                                    {i + 1} {i + 1 === 1 ? 'Guest' : 'Guests'}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <button 
                                        className="check-btn"
                                        onClick={checkAvailability}
                                        disabled={!checkIn || !checkOut}
                                    >
                                        Check Availability
                                    </button>

                                    {availability && (
                                        <div className={`availability-badge ${availability.data?.available ? 'available' : 'unavailable'}`}>
                                            {availability.data?.available ? (
                                                <>
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <polyline points="20 6 9 17 4 12"></polyline>
                                                    </svg>
                                                    <span>Available for your dates!</span>
                                                </>
                                            ) : (
                                                <>
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                                    </svg>
                                                    <span>Not available for selected dates</span>
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {availability?.data?.available && (
                                        <div className="booking-summary">
                                            <div className="summary-row">
                                                <span>{nights} Nights</span>
                                                <span>{formatPrice(currentPrice)} × {nights}</span>
                                            </div>
                                            {appliedCoupon && (
                                                <div className="summary-row">
                                                    <span>Coupon ({appliedCoupon.code})</span>
                                                    <span>-{formatPrice(couponDiscount)}</span>
                                                </div>
                                            )}
                                            <div className="summary-row total">
                                                <span>Total</span>
                                                <span>{formatPrice(totalAmount)}</span>
                                            </div>

                                            <div className="coupon-card">
                                                <div className="coupon-card-header">
                                                    <strong><i className="fas fa-ticket-alt"></i> Apply Coupon</strong>
                                                    {appliedCoupon && <span className="coupon-badge-applied">{appliedCoupon.code}</span>}
                                                </div>
                                                <div className="coupon-row">
                                                    <input
                                                        type="text"
                                                        placeholder="Enter coupon code"
                                                        value={couponCode}
                                                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                                        className="booking-input coupon-input"
                                                        disabled={couponLoading}
                                                    />
                                                    {appliedCoupon ? (
                                                        <button type="button" className="coupon-action-btn remove" onClick={handleRemoveCoupon}>
                                                            Remove
                                                        </button>
                                                    ) : (
                                                        <button type="button" className="coupon-action-btn" onClick={handleApplyCoupon} disabled={couponLoading}>
                                                            {couponLoading ? 'Applying...' : 'Apply'}
                                                        </button>
                                                    )}
                                                </div>
                                                {couponMessage && (
                                                    <div className={`coupon-message ${appliedCoupon ? 'success' : 'error'}`}>
                                                        {couponMessage}
                                                    </div>
                                                )}
                                            </div>

                                            {/* ✅ Wallet Payment Option */}
                                            {walletBalance > 0 && (
                                                <div className="wallet-option">
                                                    <label className="wallet-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            checked={useWallet}
                                                            onChange={(e) => setUseWallet(e.target.checked)}
                                                        />
                                                        <div className="wallet-info">
                                                            <strong>
                                                                <i className="fas fa-wallet"></i> 
                                                                Use Wallet Balance
                                                            </strong>
                                                            <span>Available: {formatPrice(walletBalance)}</span>
                                                        </div>
                                                    </label>
                                                    {useWallet && (
                                                        <div className="wallet-details">
                                                            <div className="wallet-row">
                                                                <span>Wallet Deduction:</span>
                                                                <span className="wallet-amount">{formatPrice(walletAmount)}</span>
                                                            </div>
                                                            <div className="wallet-row total">
                                                                <span>Amount to Pay:</span>
                                                                <span className="payable-amount">{formatPrice(payableAmount)}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            <input
                                                type="text"
                                                placeholder="Full Name *"
                                                value={bookingData.name}
                                                onChange={(e) => setBookingData({...bookingData, name: e.target.value})}
                                                className="booking-input"
                                            />
                                            <input
                                                type="email"
                                                placeholder="Email Address *"
                                                value={bookingData.email}
                                                onChange={(e) => setBookingData({...bookingData, email: e.target.value})}
                                                className="booking-input"
                                            />
                                            <input
                                                type="tel"
                                                placeholder="Phone Number *"
                                                value={bookingData.phone}
                                                onChange={(e) => setBookingData({...bookingData, phone: e.target.value})}
                                                className="booking-input"
                                            />
                                            <textarea
                                                placeholder="Special Requests (optional)"
                                                rows="3"
                                                value={bookingData.special_requests}
                                                onChange={(e) => setBookingData({...bookingData, special_requests: e.target.value})}
                                                className="booking-textarea"
                                            ></textarea>

                                            <button 
                                                className="pay-btn"
                                                onClick={handleBooking}
                                                disabled={processingPayment}
                                            >
                                                {processingPayment ? (
                                                    <>
                                                        <span className="spinner-small"></span>
                                                        Processing...
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                                                            <line x1="1" y1="10" x2="23" y2="10"></line>
                                                        </svg>
                                                        Pay {formatPrice(payableAmount)}
                                                    </>
                                                )}
                                            </button>
                                            <p className="secure-note">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                                </svg>
                                                Secure payment via Razorpay
                                                {useWallet && walletAmount > 0 && (
                                                    <span className="wallet-used-badge">
                                                        <i className="fas fa-wallet"></i> ₹{walletAmount} from wallet
                                                    </span>
                                                )}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="camp-support-box">
                                <p>Need help choosing dates?</p>
                                <a href="tel:+919335444047" className="support-phone">+91 9335444047</a>
                                <a href="mailto:bookings@redgotrips.com" className="support-email">bookings@redgotrips.com</a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            </div>
        </div>
    );
};

export default CampingDetail;

  
